import { useState, useEffect, useContext } from 'react';

import {
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Typography,
  Paper,
  Button
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import { format } from 'date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import ReceiptIcon from '@material-ui/icons/Receipt';
import { useSnackbar } from 'notistack';

import { resumeByDay } from '../../services/sales.service';
import { getProducts } from '../../services/products.service';
import { BackdropComponent } from '../commons/backdrop/backdrop.component';
import { ProductsContext } from '../../context/products.context';
import { getExpenses } from '../../services/expenses.service';

export const CutOffComponent = () => {

  const productsContext = useContext(ProductsContext);
  const { enqueueSnackbar } = useSnackbar();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [allProducts, setAllProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [money, setMoney] = useState(0);
  const [cash, setCash] = useState(0);
  const [card, setCard] = useState(0);
  const [expenses, setExpenses] = useState(0);
  const [loading, setLoading] = useState(true);

  const getProduct = (productId) => {
    return allProducts.find(product => product.id === productId);
  }

  const getResume = async (date) => {
    const resumes = await resumeByDay(date);
    const expensesItems = await getExpenses(date);
    const expensesQuantities = expensesItems.filter(expense => !expense.canceled).map(exp => +exp.quantity);
    const expensesTotal = expensesQuantities.length > 0 ? expensesQuantities.reduce((total, current) => total + current) : 0;
    setExpenses(expensesTotal);
    if (resumes.length === 0) {
      setOrders([]);
      setMoney(0);
      setCard(0);
      setCash(0);
      enqueueSnackbar(`No se encontraron ventas`, {
        variant: 'warning',
        resumeHideDuration: 4000,
        anchorOrigin:
          { horizontal: 'right', vertical: 'bottom' },
      });
      return;
    }
    const productsMap = {};
    let total = 0;
    let card = 0;
    let cash = 0;
    for (const resume of resumes) {
      total += resume.total + (resume.extra || 0);
      if (resume.paymentMethod === 'cash') {
        cash += resume.total + (resume.extra || 0);
      } else {
        card += resume.total + (resume.extra || 0);
      }
      for (const item of resume.items) {
        if (productsMap[item.product]) {
          productsMap[item.product] = productsMap[item.product] + item.quantity;
        } else {
          productsMap[item.product] = item.quantity
        }
      }
    }
    total = total - expensesTotal;
    const products = Object.keys(productsMap).map(product => ({
      product: getProduct(product),
      quantity: productsMap[product]
    })).sort((a, b) => b.quantity - a.quantity);
    setOrders(products);
    setMoney(total);
    setCash(cash);
    setCard(card);
    enqueueSnackbar(`Corte de ventas realizado`, {
      variant: 'success',
      resumeHideDuration: 3000,
      anchorOrigin:
        { horizontal: 'right', vertical: 'bottom' },
    });
  }

  const fetchProducts = async () => {
    const products = await getProducts();
    productsContext.handleProducts(products);
    setAllProducts(products);
    setLoading(false);
  }

  useEffect(() => {
    if (productsContext.products.length === 0) {
      fetchProducts();
    } else {
      setAllProducts(productsContext.products);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loading ? (
    <BackdropComponent />
  ) : (
    <Grid container justifyContent="center">
      <Grid item xs={12} lg={8}>
        <Grid container justifyContent="center" spacing={2}>
          <Grid item xs={10}>
            <Typography variant="h3" align="center">
              Corte de venta
            </Typography>
          </Grid>
          <Grid item xs={12} lg={10}>
            <Grid container justifyContent="center" alignItems="center" spacing={2}>
              <Grid item xs={5}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="dd/MM/yyyy"
                    margin="normal"
                    id="date-picker-inline"
                    label="Seleccionar fecha de corte"
                    value={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    fullWidth
                  />
                </MuiPickersUtilsProvider>
              </Grid>
              <Grid item xs={5}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<ReceiptIcon />}
                  onClick={() => getResume(selectedDate)}
                >
                  Generar corte
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={10}>
            <Typography variant="h5" align="center">
              Corte del {format(selectedDate, 'dd/MM/yyyy')}
            </Typography>
            <TableContainer component={Paper}>
              <Table aria-label="orders">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Precio</TableCell>
                    <TableCell align="right">Importe</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length > 0 && orders.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        {row.product.name}
                      </TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                      <TableCell align="right">${row.product.price}</TableCell>
                      <TableCell align="right">${row.product.price * row.quantity}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4} align="right">Efectivo: ${cash}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} align="right">Tarjeta crédito o débito: ${card}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} align="right">Gastos: ${expenses}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} align="right">Total: ${money}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}